import { IAddress } from "../types/seed";

// Brazilian states list for validation
export const BRAZILIAN_STATES = [
  { code: "AC", name: "Acre" },
  { code: "AL", name: "Alagoas" },
  { code: "AP", name: "Amapá" },
  { code: "AM", name: "Amazonas" },
  { code: "BA", name: "Bahia" },
  { code: "CE", name: "Ceará" },
  { code: "DF", name: "Distrito Federal" },
  { code: "ES", name: "Espírito Santo" },
  { code: "GO", name: "Goiás" },
  { code: "MA", name: "Maranhão" },
  { code: "MT", name: "Mato Grosso" },
  { code: "MS", name: "Mato Grosso do Sul" },
  { code: "MG", name: "Minas Gerais" },
  { code: "PA", name: "Pará" },
  { code: "PB", name: "Paraíba" },
  { code: "PR", name: "Paraná" },
  { code: "PE", name: "Pernambuco" },
  { code: "PI", name: "Piauí" },
  { code: "RJ", name: "Rio de Janeiro" },
  { code: "RN", name: "Rio Grande do Norte" },
  { code: "RS", name: "Rio Grande do Sul" },
  { code: "RO", name: "Rondônia" },
  { code: "RR", name: "Roraima" },
  { code: "SC", name: "Santa Catarina" },
  { code: "SP", name: "São Paulo" },
  { code: "SE", name: "Sergipe" },
  { code: "TO", name: "Tocantins" },
];

export interface ViaCEPResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
  erro?: boolean;
}

export interface AddressData {
  street: string;
  neighborhood: string;
  city: string;
  state: string;
}

/**
 * Fetches address data from ViaCEP API based on postal code
 */
export const fetchAddressFromCEP = async (
  cep: string,
): Promise<AddressData | null> => {
  try {
    // Clean CEP (remove non-digits)
    const cleanCep = cep.replace(/\D/g, "");

    // Validate CEP format (8 digits)
    if (cleanCep.length !== 8) {
      return null;
    }

    const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);

    if (!response.ok) {
      return null;
    }

    const data: ViaCEPResponse = await response.json();

    if (data.erro) {
      return null;
    }

    return {
      street: data.logradouro || "",
      neighborhood: data.bairro || "",
      city: data.localidade || "",
      state: data.uf || "",
    };
  } catch (error) {
    console.error("Error fetching address from CEP:", error);
    return null;
  }
};

/**
 * Validates if a state code is valid for Brazil
 */
export const isValidBrazilianState = (stateCode: string): boolean => {
  const upperStateCode = stateCode.toUpperCase();
  return BRAZILIAN_STATES.some((state) => state.code === upperStateCode);
};

/**
 * Formats CEP to Brazilian standard (00000-000)
 */
export const formatCEP = (cep: string): string => {
  const cleanCep = cep.replace(/\D/g, "");
  if (cleanCep.length === 8) {
    return `${cleanCep.slice(0, 5)}-${cleanCep.slice(5)}`;
  }
  return cep;
};

/**
 * Validates CEP format
 */
export const isValidCEP = (cep: string): boolean => {
  const cleanCep = cep.replace(/\D/g, "");
  return cleanCep.length === 8;
};

/**
 * Formats an address object to a readable string
 * Similar to the backend Address model's __str__ method
 */
export const formatAddress = (address: IAddress): string => {
  if (!address) return "";

  if (address.address_type === "coordinates") {
    const location = address.location_reference || "Localização rural";
    return `${location} • ${address.city}/${address.state}`;
  }

  // Line 1: Street + number + complement
  const base = `${address.street}, ${address.number}`;
  const line1 = address.complement ? `${base} - ${address.complement}` : base;

  // Line 2: Neighborhood + city/state + postal_code
  const line2 = `${address.neighborhood} - ${address.city}/${address.state} - ${address.postal_code}`;

  return `${line1} • ${line2}`;
};

/**
 * Gets the best available address (address_obj or fallback to simple address)
 */
export const getBestAddress = (
  address_obj: IAddress | null | undefined,
  fallbackAddress: string,
): string => {
  if (address_obj) {
    return formatAddress(address_obj);
  }
  return fallbackAddress || "";
};
